/**
 * AgentName — shows an agent's name for an agent id.
 *
 * Rows from the QA, sentiment and compliance services carry only the agent id;
 * names live in the analytics agent profiles. Every AgentName on a page is
 * resolved in one batched request, and an id is looked up once per session.
 * Without a profile (or outside the provider) the id itself is shown.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getAgentProfilesByIds } from '../../services/analyticsService';

// The profiles endpoint names at most this many agents per request
const BATCH_SIZE = 100;

const AgentNamesContext = createContext({ names: {}, request: () => {}, forget: () => {} });

export const AgentNamesProvider = ({ children }) => {
  const [names, setNames] = useState({});
  const requested = useRef(new Set());
  const queue = useRef(new Set());
  const timer = useRef(null);

  const flush = useCallback(async () => {
    timer.current = null;
    const ids = [...queue.current];
    queue.current = new Set();
    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      const batch = ids.slice(i, i + BATCH_SIZE);
      const result = await getAgentProfilesByIds(batch);
      if (result.success) {
        const found = Object.fromEntries(result.data.map((profile) => [profile.agent_id, profile.name]));
        setNames((current) => ({ ...current, ...found }));
      } else {
        // Not remembered as asked, so a later render tries again
        batch.forEach((id) => requested.current.delete(id));
      }
    }
  }, []);

  const request = useCallback((agentId) => {
    if (!agentId || requested.current.has(agentId)) return;
    requested.current.add(agentId);
    queue.current.add(agentId);
    // Requests made while one page renders leave together
    if (!timer.current) timer.current = setTimeout(flush, 0);
  }, [flush]);

  // After a profile edit: drop the name so it is looked up again
  const forget = useCallback((agentId) => {
    requested.current.delete(agentId);
    setNames(({ [agentId]: dropped, ...rest }) => rest);
  }, []);

  const value = useMemo(() => ({ names, request, forget }), [names, request, forget]);
  return <AgentNamesContext.Provider value={value}>{children}</AgentNamesContext.Provider>;
};

export const useAgentNames = () => useContext(AgentNamesContext);

const AgentName = ({ id }) => {
  const { names, request } = useAgentNames();
  useEffect(() => { request(id); }, [id, names, request]);

  const name = names[id] || id;
  return <span title={name !== id ? id : undefined}>{name}</span>;
};

export default AgentName;
